import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { theme, cardStyle, inputStyle, btnStyle } from '../theme';

const STATUSES = ['waiting', 'contacted', 'scheduled', 'cancelled'];

const WaitListPage = ({ API_BASE }) => {
  const [list, setList] = useState([]);
  const [patients, setPatients] = useState([]);
  const [statusFilter, setStatusFilter] = useState('waiting');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ patientId:'', reason:'', preferredDate:'', preferredSlot:'', notes:'' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = statusFilter ? `/waitlist?status=${statusFilter}` : '/waitlist';
      const [w, p] = await Promise.all([
        axios.get(`${API_BASE}${url}`),
        axios.get(`${API_BASE}/patients`).catch(() => ({ data: [] })),
      ]);
      setList(w.data || []);
      setPatients((p.data || []).slice(0, 500));
    } catch (e) { alert('Load failed: ' + (e.response?.data?.error || e.message)); }
    finally { setLoading(false); }
  }, [API_BASE, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.patientId) return alert('Select a patient');
    try {
      await axios.post(`${API_BASE}/waitlist/add`, form);
      setForm({ patientId:'', reason:'', preferredDate:'', preferredSlot:'', notes:'' });
      load();
    } catch (e) { alert('Failed: ' + (e.response?.data?.error || e.message)); }
  };

  const update = async (id, status) => {
    try {
      await axios.put(`${API_BASE}/waitlist/${id}`, { status });
      load();
    } catch (e) { alert('Failed: ' + (e.response?.data?.error || e.message)); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove from wait list?')) return;
    try {
      await axios.delete(`${API_BASE}/waitlist/${id}`);
      load();
    } catch (e) { alert('Failed: ' + (e.response?.data?.error || e.message)); }
  };

  const badge = (s) => {
    const colors = {
      waiting:   { bg:'#fef3c7', fg:'#92400e' },
      contacted: { bg:'#dbeafe', fg:'#1d4ed8' },
      scheduled: { bg:'#dcfce7', fg:'#166534' },
      cancelled: { bg:'#f1f5f9', fg:'#475569' },
    };
    const c = colors[s] || { bg:'#f1f5f9', fg:'#475569' };
    return <span style={{ padding:'3px 10px', borderRadius:12, fontSize:'0.72rem', fontWeight:700, background:c.bg, color:c.fg }}>{s}</span>;
  };

  return (
    <div style={{ padding:20 }}>
      <h2 style={{ color: theme.navy, margin:'0 0 12px' }}>⏳ Appointment Wait List</h2>
      <div style={{ ...cardStyle, marginBottom:12, background: theme.primaryLight, border:`1px solid ${theme.primary}` }}>
        <div style={{ fontSize:'0.85rem', color:theme.navy, lineHeight:1.5 }}>
          <b>How to use:</b> Add patients who want earlier slots. When a cancellation happens, contact them to fill the gap. Track status: <i>waiting → contacted → scheduled</i>.
        </div>
      </div>

      <div style={{ ...cardStyle, marginBottom:12 }}>
        <h4 style={{ margin:'0 0 10px', color:theme.navy }}>Add Patient to Wait List</h4>
        <div style={{ display:'grid', gridTemplateColumns:'2fr 1.5fr 1fr 1fr 2fr auto', gap:8, alignItems:'center' }}>
          <select value={form.patientId} onChange={e=>setForm({...form, patientId:e.target.value})} style={inputStyle}>
            <option value="">— Select patient —</option>
            {patients.map(p => <option key={p._id} value={p._id}>{p.name} ({p.phone || p.uhid || '—'})</option>)}
          </select>
          <input placeholder="Reason / treatment" value={form.reason} onChange={e=>setForm({...form, reason:e.target.value})} style={inputStyle}/>
          <input type="date" value={form.preferredDate} onChange={e=>setForm({...form, preferredDate:e.target.value})} style={inputStyle}/>
          <input placeholder="Preferred time" value={form.preferredSlot} onChange={e=>setForm({...form, preferredSlot:e.target.value})} style={inputStyle}/>
          <input placeholder="Notes" value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})} style={inputStyle}/>
          <button onClick={add} style={{ ...btnStyle, background:theme.primary, color:'white' }}>+ Add</button>
        </div>
      </div>

      <div style={{ display:'flex', gap:8, marginBottom:12 }}>
        {['', ...STATUSES].map(s => (
          <button key={s||'all'} onClick={()=>setStatusFilter(s)}
            style={{ padding:'6px 14px', fontSize:'0.8rem', borderRadius:'6px', border:'1px solid #e5e7eb', cursor:'pointer',
              background: statusFilter===s ? theme.primary : 'white', color: statusFilter===s ? 'white' : '#333', fontWeight:600 }}>
            {s || 'all'} ({statusFilter === s ? list.length : ''})
          </button>
        ))}
      </div>

      <div style={{ ...cardStyle, padding:0, overflow:'hidden' }}>
        {loading ? <div style={{ padding:40, textAlign:'center', color:'#888' }}>Loading…</div>
         : list.length === 0 ? <div style={{ padding:40, textAlign:'center', color:'#888' }}>No patients on wait list.</div>
         : (
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr style={{ background:'#f9fafb' }}>
              <th style={th}>Patient</th><th style={th}>Reason</th>
              <th style={th}>Preferred</th><th style={th}>Notes</th>
              <th style={th}>Status</th><th style={th}>Added</th><th style={th}>Actions</th>
            </tr></thead>
            <tbody>
              {list.map(w => (
                <tr key={w._id} style={{ borderBottom:'1px solid #f1f3f5' }}>
                  <td style={{ ...td, fontWeight:600 }}>
                    {w.patientId?.name || '—'}
                    <div style={{ fontSize:'0.72rem', color:'#666' }}>{w.patientId?.phone}</div>
                  </td>
                  <td style={td}>{w.reason || '—'}</td>
                  <td style={td}>
                    {w.preferredDate || '—'}
                    {w.preferredSlot && <div style={{ fontSize:'0.72rem', color:'#666' }}>{w.preferredSlot}</div>}
                  </td>
                  <td style={{ ...td, maxWidth:200, whiteSpace:'normal' }}>{w.notes || '—'}</td>
                  <td style={td}>{badge(w.status)}</td>
                  <td style={{ ...td, fontSize:'0.75rem', color:'#888' }}>{new Date(w.createdAt).toLocaleDateString('en-IN')}</td>
                  <td style={td}>
                    <select value={w.status} onChange={e=>update(w._id, e.target.value)}
                      style={{ fontSize:'0.75rem', padding:'4px 8px', borderRadius:6, border:'1px solid #e5e7eb', marginRight:4 }}>
                      {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button onClick={()=>remove(w._id)} style={{ padding:'4px 10px', fontSize:'0.75rem', background:theme.danger, color:'white', border:'none', borderRadius:6, cursor:'pointer' }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
         )}
      </div>
    </div>
  );
};

const th = { padding:'10px 12px', textAlign:'left', fontWeight:600, fontSize:'0.8rem', color:'#555', borderBottom:'1px solid #e5e7eb' };
const td = { padding:'10px 12px', fontSize:'0.85rem', color:'#333' };

export default WaitListPage;
